import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useUI, TabKey } from './store'
import { DEFAULT_WRITER_SYSTEM_PROMPT } from './prompts'
import { startSearch, startOutline, startWrite, stopStream, getBuffered } from './ws'
import { autoScrollToBottom, copyToClipboard, downloadText } from './utils'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

function Toolbar() {
  const { activeTab, streamedCount, isStreaming, autoAdvance, autoCopyToFinal } = useUI()
  const setAutoAdvance = useUI(s => s.setAutoAdvance)
  const setAutoCopyToFinal = useUI(s => s.setAutoCopyToFinal)
  const resetAll = useUI(s => s.resetAll)
  const logout = useUI(s => s.logout)
  const onStop = () => {
    stopStream()
    useUI.setState({ isStreaming: false })
  }
  const onClearAll = () => {
    if (confirm('This will stop any running stream and clear ALL inputs, outputs, logs, and settings. Continue?')) {
      try { onStop() } catch {}
      resetAll()
    }
  }
  const onLogout = () => {
    if (confirm('Sign out and remove the password from this browser?')) {
      try { onStop() } catch {}
      logout()
    }
  }
  const stage = useMemo(() => {
    if (activeTab === 'search') return 'Stage: Search'
    if (activeTab === 'outline') return 'Stage: Outline'
    if (activeTab === 'write') return 'Stage: Write'
    return 'Final'
  }, [activeTab])
  return (
    <div className="flex items-center justify-between p-2 border-b border-zinc-800 bg-zinc-900 sticky top-0 z-10">
      <div className="text-sm font-semibold text-zinc-200">{stage}</div>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input type="checkbox" checked={autoAdvance} onChange={e=>setAutoAdvance(e.target.checked)} />
          Auto-advance
        </label>
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input type="checkbox" checked={autoCopyToFinal} onChange={e=>setAutoCopyToFinal(e.target.checked)} />
          Auto-copy Writing to Final (live)
        </label>
        <div className="text-sm text-zinc-300">Chars: {streamedCount}</div>
        <button className="btn btn-outline" onClick={onStop} disabled={!isStreaming} title="Stop current stream">Stop</button>
        <button className="btn btn-secondary" onClick={onClearAll} title="Clear EVERYTHING (inputs, outputs, logs, settings)">Clear Everything</button>
        <button className="btn btn-outline" onClick={onLogout} title="Sign out and clear password">Logout</button>
      </div>
    </div>
  )
}

function ThemeBar() {
  const theme = useUI(s => s.theme)
  const setTheme = useUI(s => s.setTheme)
  return (
    <div className="p-3 border-b border-zinc-800 bg-zinc-900">
      <label className="label">Theme</label>
      <input className="input mt-1" placeholder="Enter your theme..." value={theme} onChange={e=>setTheme(e.target.value)} />
      <div className="small-muted mt-1">This theme is shared across all tabs.</div>
    </div>
  )
}

function Tabs() {
  const activeTab = useUI(s => s.activeTab)
  const setActiveTab = useUI(s => s.setActiveTab)
  return (
    <div className="tabs p-3">
      <div className="tabs-list">
        {(['search','outline','write','final'] as TabKey[]).map(t => (
          <button key={t} className="tabs-trigger" data-state={activeTab===t? 'active': 'inactive'} onClick={()=>setActiveTab(t)}>
            {t[0].toUpperCase()+t.slice(1)}
          </button>
        ))}
      </div>
      <div className="tabs-content">
        {activeTab === 'search' && <SearchTab/>}
        {activeTab === 'outline' && <OutlineTab/>}
        {activeTab === 'write' && <WriteTab/>}
        {activeTab === 'final' && <FinalTab/>}
      </div>
    </div>
  )
}

function LogBox() {
  const log = useUI(s => s.progressLog)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(()=>{ autoScrollToBottom(ref.current) }, [log])
  return (
    <div>
      <div className="label mb-1">Stream log</div>
      <div ref={ref} className="logbox">{log.map((l,i)=>(<div key={i}>{l}</div>))}</div>
    </div>
  )
}

function SearchTab() {
  const theme = useUI(s => s.theme)
  const setSearchText = useUI(s => s.setSearchText)
  const searchText = useUI(s => s.searchText)
  const isStreaming = useUI(s => s.isStreaming)
  const setIsStreaming = useUI(s => s.setIsStreaming)
  const setStreamedCount = useUI(s => s.setStreamedCount)
  const appendLog = useUI(s => s.appendLog)
  const clearLog = useUI(s => s.clearLog)
  const setActiveTab = useUI(s => s.setActiveTab)
  const autoAdvance = useUI(s => s.autoAdvance)
  const outRef = useRef<HTMLDivElement>(null)

  useEffect(()=>{ autoScrollToBottom(outRef.current) }, [searchText])

  const onStart = () => {
    if (!theme.trim()) {
      alert('Please enter a Theme before starting Search.')
      return
    }
    setSearchText('')
    clearLog()
    setStreamedCount(0)
    setIsStreaming(true)
    startSearch(theme, {
      onStarted: () => appendLog('search: started'),
      onProgress: (ev, text, count) => {
        appendLog(`progress: ${ev}${text ? ` | ${text}`: ''}`)
        if (count!=null) setStreamedCount(count)
      },
      onDelta: (d, count) => {
        setSearchText(useUI.getState().searchText + d)
        if (count!=null) setStreamedCount(count)
      },
      onDone: (count) => {
        setIsStreaming(false)
        if (count!=null) setStreamedCount(count)
        appendLog('search: done')
        if (autoAdvance) setActiveTab('outline')
      },
      onError: (err, details) => {
        setIsStreaming(false)
        appendLog(`error: ${err}${details? ` | ${details}`:''}`)
        alert(`Search failed: ${err}`)
      }
    })
  }
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="flex flex-col gap-3">
        <div className="small-muted">Current Theme: {theme || '(empty)'} </div>
        <div className="flex gap-2">
          <button className="btn" onClick={onStart} disabled={isStreaming}>Start Search</button>
          <button className="btn btn-secondary" onClick={()=>{ setSearchText(''); clearLog(); setStreamedCount(0) }} disabled={isStreaming}>Clear Output</button>
          <button className="btn btn-outline" onClick={()=>copyToClipboard(searchText)} disabled={!searchText}>Copy Output</button>
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <div>
          <div className="label mb-1">Output</div>
          <div ref={outRef} className="scrollbox">{searchText}</div>
        </div>
        <LogBox/>
      </div>
    </div>
  )
}

function OutlineTab() {
  const theme = useUI(s => s.theme)
  const searchText = useUI(s => s.searchText)
  const outlinePlan = useUI(s => s.outlinePlan)
  const setOutlinePlan = useUI(s => s.setOutlinePlan)
  const isStreaming = useUI(s => s.isStreaming)
  const setIsStreaming = useUI(s => s.setIsStreaming)
  const setStreamedCount = useUI(s => s.setStreamedCount)
  const appendLog = useUI(s => s.appendLog)
  const clearLog = useUI(s => s.clearLog)
  const setActiveTab = useUI(s => s.setActiveTab)
  const autoAdvance = useUI(s => s.autoAdvance)
  const outRef = useRef<HTMLDivElement>(null)
  const [data, setData] = useState(searchText)

  useEffect(()=>{ if (!isStreaming) setData(searchText) }, [searchText])
  useEffect(()=>{ autoScrollToBottom(outRef.current) }, [outlinePlan])

  const onStart = () => {
    if (!theme.trim()) { alert('Please enter a Theme before starting Outline.'); return }
    setOutlinePlan('')
    clearLog()
    setStreamedCount(0)
    setIsStreaming(true)
    startOutline(theme, data, {
      onStarted: () => appendLog('outline: started'),
      onProgress: (ev, text, count) => {
        appendLog(`progress: ${ev}${text ? ` | ${text}`: ''}`)
        if (count!=null) setStreamedCount(count)
      },
      onDelta: (d, count) => {
        setOutlinePlan(useUI.getState().outlinePlan + d)
        if (count!=null) setStreamedCount(count)
      },
      onDone: (count) => {
        setIsStreaming(false)
        if (count!=null) setStreamedCount(count)
        appendLog('outline: done')
        if (autoAdvance) setActiveTab('write')
      },
      onError: (err, details) => {
        setIsStreaming(false)
        appendLog(`error: ${err}${details? ` | ${details}`:''}`)
        alert(`Outline failed: ${err}`)
      }
    })
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="flex flex-col gap-3">
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <div className="label">Theme</div>
            <input className="input" value={theme} readOnly />
            <div className="small-muted">Edit the Theme in the global input above tabs.</div>
          </div>
          <button className="btn btn-outline" onClick={()=>setData(searchText)} disabled={isStreaming}>Reset Data from Search</button>
        </div>
        <div>
          <div className="label mb-1">Data</div>
          <textarea className="textarea textarea-mono h-48" value={data} onChange={e=>setData(e.target.value)} />
        </div>
        <div className="flex gap-2">
          <button className="btn" onClick={onStart} disabled={isStreaming}>Start Outline</button>
          <button className="btn btn-secondary" onClick={()=>{ setOutlinePlan(''); clearLog(); setStreamedCount(0) }} disabled={isStreaming}>Clear Output</button>
          <button className="btn btn-outline" onClick={()=>copyToClipboard(outlinePlan)} disabled={!outlinePlan}>Copy Output</button>
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <div>
          <div className="label mb-1">Output</div>
          <div ref={outRef} className="scrollbox">{outlinePlan}</div>
        </div>
        <LogBox/>
      </div>
    </div>
  )
}

function WriteTab() {
  const outlinePlan = useUI(s => s.outlinePlan)
  const articleText = useUI(s => s.articleText)
  const setArticleText = useUI(s => s.setArticleText)
  const finalText = useUI(s => s.finalText)
  const setFinalText = useUI(s => s.setFinalText)
  const systemPrompt = useUI(s => s.systemPrompt)
  const setSystemPrompt = useUI(s => s.setSystemPrompt)
  const theme = useUI(s => s.theme)
  const searchText = useUI(s => s.searchText)
  const isStreaming = useUI(s => s.isStreaming)
  const setIsStreaming = useUI(s => s.setIsStreaming)
  const setStreamedCount = useUI(s => s.setStreamedCount)
  const appendLog = useUI(s => s.appendLog)
  const clearLog = useUI(s => s.clearLog)
  const setActiveTab = useUI(s => s.setActiveTab)
  const autoAdvance = useUI(s => s.autoAdvance)
  const autoCopyToFinal = useUI(s => s.autoCopyToFinal)
  const [plan, setPlan] = useState(outlinePlan)
  const outRef = useRef<HTMLDivElement>(null)

  useEffect(()=>{ setPlan(outlinePlan) }, [outlinePlan])
  useEffect(()=>{ autoScrollToBottom(outRef.current) }, [articleText])

  const onStart = () => {
    if (!plan.trim()) { alert('Please provide an Editorial Plan before starting Write.'); return }
    setArticleText('')
    setStreamedCount(0)
    if (autoCopyToFinal) setFinalText('')
    clearLog()
    setIsStreaming(true)
    if (autoAdvance && autoCopyToFinal) setActiveTab('final')
    {
      const title = (theme || '').trim() || undefined
      startWrite(plan, systemPrompt || undefined, theme, searchText, title, {
        onStarted: () => appendLog('write: started'),
        onProgress: (ev, text, count) => {
          appendLog(`progress: ${ev}${text ? ` | ${text}`: ''}`)
          if (count!=null) setStreamedCount(count)
        },
        onDelta: (d, count) => {
          const curr = useUI.getState().articleText + d
          setArticleText(curr)
          if (autoCopyToFinal) setFinalText(useUI.getState().finalText + d)
          if (count!=null) setStreamedCount(count)
        },
        onDone: (count) => {
          setIsStreaming(false)
          if (count!=null) setStreamedCount(count)
          const full = getBuffered()
          setFinalText(full)
          appendLog('write: done')
          if (autoAdvance) setActiveTab('final')
        },
        onError: (err, details) => {
          setIsStreaming(false)
          const partial = getBuffered()
          if (partial) setFinalText(partial)
          appendLog(`error: ${err}${details? ` | ${details}`:''}`)
          alert(`Write failed: ${err}`)
        }
      })
    }
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="flex flex-col gap-3">
        <div>
          <div className="label mb-1">Editorial Plan</div>
          <textarea className="textarea textarea-mono h-48" value={plan} onChange={e=>setPlan(e.target.value)} />
        </div>
        <div>
          <div className="label mb-1">Optional System Prompt</div>
          <textarea className="textarea textarea-mono h-32" value={systemPrompt} onChange={e=>setSystemPrompt(e.target.value)} placeholder="Defaults provided" />
          {!systemPrompt && (
            <div className="small-muted mt-1">Using default prompt.</div>
          )}
        </div>
        <div className="flex gap-2">
          <button className="btn" onClick={onStart} disabled={isStreaming}>Start Write</button>
          <button className="btn btn-secondary" onClick={()=> setArticleText('')} disabled={isStreaming}>Clear Output</button>
          <button className="btn btn-outline" onClick={()=>{ setFinalText(articleText); useUI.getState().setActiveTab('final') }} disabled={!articleText}>Send to Final</button>
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <div>
          <div className="label mb-1">Article (live)</div>
          <div ref={outRef} className="scrollbox">{articleText}</div>
        </div>
        <LogBox/>
      </div>
    </div>
  )
}

function FinalTab() {
  const finalText = useUI(s => s.finalText)
  const setFinalText = useUI(s => s.setFinalText)
  const [filename, setFilename] = useState('article.md')
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="flex flex-col gap-3">
        <div>
          <div className="label mb-1">Markdown</div>
          <textarea className="textarea textarea-mono h-72" value={finalText} onChange={e=>setFinalText(e.target.value)} />
          <div className="small-muted mt-1">Edits here will not change the Write tab text. This is the canonical output for export.</div>
        </div>
        <div className="">
          <div className="label mb-1">File name</div>
          <input className="input" value={filename} onChange={e=>setFilename(e.target.value)} />
          <div className="flex gap-2 mt-2">
            <button className="btn btn-secondary" onClick={()=>useUI.getState().setFinalText('')}>Clear</button>
            <button className="btn btn-outline" onClick={()=>copyToClipboard(finalText)} disabled={!finalText}>Copy Markdown</button>
            <button className="btn" onClick={()=>downloadText(filename || 'article.md', finalText)} disabled={!finalText}>Download .md</button>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <div>
          <div className="label mb-1">Preview</div>
          <div className="card p-4 prose prose-invert max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{finalText || '*Nothing to preview yet.*'}</ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  )
}

function Login() {
  const setAuthToken = useUI(s => s.setAuthToken)
  const setIsAuthed = useUI(s => s.setIsAuthed)
  const [password, setPassword] = useState('')
  const [checking, setChecking] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    // Detect if backend has auth disabled
    (async () => {
      try {
        const res = await fetch('/health', { credentials: 'same-origin' })
        if (res.ok) {
          const js = await res.json().catch(() => ({}))
          if (!js || js.auth === 'none') {
            setIsAuthed(true)
            setChecking(false)
            return
          }
        }
      } catch {}
      setChecking(false)
    })()
  }, [])

  async function onSubmit(e?: React.FormEvent) {
    e?.preventDefault()
    setError('')
    const pwd = password.trim()
    if (!pwd) { setError('Enter password'); return }
    try {
      const res = await fetch('/health', {
        headers: { Authorization: `Bearer ${pwd}` },
        credentials: 'same-origin'
      })
      if (!res.ok) throw new Error('Unauthorized')
      setAuthToken(pwd)
      setIsAuthed(true)
    } catch (e: any) {
      setError('Invalid password')
    }
  }

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-zinc-100">
        <div className="text-zinc-300">Checking authentication…</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-zinc-100 p-4">
      <form onSubmit={onSubmit} className="card w-full max-w-sm p-6">
        <h2 className="text-lg font-semibold mb-4">EGSH v3 — Sign in</h2>
        <label className="label">Password</label>
        <input
          type="password"
          className="input mt-1"
          placeholder="Enter password"
          value={password}
          onChange={(e)=>setPassword(e.target.value)}
        />
        {error && <div className="text-red-400 text-sm mt-2">{error}</div>}
        <button type="submit" className="btn w-full mt-4">Sign in</button>
        <div className="small-muted mt-2">Please provide a password.</div>
      </form>
    </div>
  )
}

export default function App() {
  const isAuthed = useUI(s => s.isAuthed)
  const logout = useUI(s => s.logout)
  const authToken = useUI(s => s.authToken)

  // Probe backend to see if auth is required. If required and we don't have a token, force Login.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/health', { credentials: 'same-origin' })
        if (res.status === 401) {
          useUI.setState({ isAuthed: false })
          return
        }
        if (res.ok) {
          const js = await res.json().catch(() => ({}))
          if (js && js.auth === 'required' && !useUI.getState().authToken) {
            useUI.setState({ isAuthed: false })
          }
        }
      } catch {
        // Network issues: do nothing, keep current state
      }
    })()
  }, [])

  useEffect(() => {
    // If we have a stored token, try to auto-validate silently
    const token = useUI.getState().authToken
    if (token && !isAuthed) {
      ;(async () => {
        try {
          const res = await fetch('/health', { headers: { Authorization: `Bearer ${token}` } })
          if (res.ok) useUI.setState({ isAuthed: true })
          else logout()
        } catch { logout() }
      })()
    }
  }, [isAuthed, logout])

  if (!isAuthed) return <Login />

  return (
    <div className="min-h-screen flex flex-col bg-zinc-950 text-zinc-100">
      <div className="p-3 border-b border-zinc-800 bg-zinc-900">
        <h1 className="text-lg font-bold">EGSH v3</h1>
      </div>
      <Toolbar/>
      <ThemeBar/>
      <div className="flex-1 p-3">
        <Tabs/>
      </div>
    </div>
  )
}
